/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {ClickAwayListener, Paper, TextField} from '@material-ui/core';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import debounce from 'lodash.debounce';
import {translate} from '@ion/core/src/locale/locale';
import {TopNavSearchIcon} from '@ion/dls/src/icons/TopNavSearchIcon';
import ConditionalRender from '@ion/dls/src/render/ConditionalRender';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {useInfiniteGroupsQuery} from '@ion/query/src/hooks/useInfiniteGroupsQuery';
import {QueryChipData, SearchQueryValue, SuggestionsType} from '../../search/types';
import DisplayNoGroupsButton from './DisplayNoGroupsButton';
import GroupSearchDropdown from './GroupSearchDropdown';
import {validateGroupFilterActive} from './helpers';

export interface GroupSearchInputProps {
  accountId: number;
  placeholder: string;
  onMenuSelection: Function;
  accountTree: any;
  viewSubGroups: boolean;
  onQueryRemove: (key: string) => () => void;
  queries: QueryChipData[];
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    searchSuggestionInputWrapper : {
      maxWidth : '500px',
      width    : '100%',
      position : 'relative'
    },
    searchSuggestionInputRoot : {
      height             : '48px',
      '& .MuiInput-root' : {
        minHeight : '48px'
      },
      '& .MuiFormHelperText-root' : {
        position : 'absolute !important',
        bottom   : '-20px !important'
      }
    },
    searchSuggestionInputUnderline : {
      padding     : '12px !important',
      height      : 'inherit',
      maxHeight   : '48px',
      '&::before' : {
        borderBottom : 'transparent'
      },
      '&:hover' : {
        '&::before' : {
          borderBottom : `2px solid ${dlsColor('aaBlue')} !important`
        }
      },
      '& .MuiInput-root' : {
        minHeight : '48px'
      }
    },
    suggestionsWrapper : {
      zIndex    : 5,
      maxWidth  : 500,
      border    : 'none !important',
      top       : '65px !important',
      transform : 'translate3d(0px, 160px, 0px) !important',
      left      : 'unset !important',
      right     : 'unset !important',
      width     : '100%'
    },
    searchIcon : {
      display        : 'flex',
      justifyContent : 'center',
      alignItems     : 'center'
    },
    input : {
      fontSize   : 14,
      fontFamily : theme.typography.fontFamily
    }
  };
});

/**
 * @GroupSearchInput
 * Is a component that allows the user to search for groups via the ModularSearchWidget.
 *
 * ```typescript
 * import GroupSearchInput from './groups/GroupSearchInput';
 *
 * ...
 *
 * <GroupSearchInput accountId={212900}
 *   placeholder={"Search for groups"}
 *   onMenuSelection={onMenuSelection}
 *   accountTree={accountTree}
 *   viewSubGroups={true} />
 *
 * ```
 *
 * @param accountId         - The account ID to search for groups
 * @param placeholder       - The placeholder text for the input field
 * @param onMenuSelection   - The function to call when a menu item is selected
 * @param accountTree       - The account tree
 * @param viewSubGroups     - A boolean to determine if subgroups should be included in the search
 */
const GroupSearchInput = ({
  accountId,
  placeholder,
  onMenuSelection,
  accountTree,
  viewSubGroups,
  onQueryRemove,
  queries
}: GroupSearchInputProps) => {
  const classes                         = useStyles();
  const [searchString, setSearchString] = useState('');
  const [inputValue, setInputValue]     = useState('');
  const isValidStringLength             = searchString?.length >= 2;
  const groupFilterActive               = useMemo(() => validateGroupFilterActive(queries), [queries]);

  const onSelection = useCallback(function(item: string | SearchQueryValue) {
    return (event?: any) => {
      event?.preventDefault();
      onMenuSelection(item, SuggestionsType.LOOKUP, event);
    };
  }, []);

  const debouncedSetSearchString = useCallback(debounce((value: string) => {
    setSearchString(value);
  }, 500), []);

  const handleInputChange = function(event) {
    const newValue = event.target.value;
    setInputValue(newValue);
    debouncedSetSearchString(newValue);
  };

  const handleClickAway = useCallback(function() {
    setSearchString('');
    setInputValue('');
  }, []);

  const {
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading,
    data
  } = useInfiniteGroupsQuery({
    accountId,
    searchString,
    includeSubGroups : viewSubGroups,
    enabled          : searchString?.length >= 2
  });

  return (
    <div>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>
          <TextField className={classes.searchSuggestionInputRoot}
            autoComplete="off"
            placeholder={placeholder}
            helperText={translate('common:modularsearch.groupsHelperText')}
            value={inputValue}
            onChange={handleInputChange}
            InputProps={{
              classes : {
                input     : classes.input,
                underline : classes.searchSuggestionInputUnderline
              },
              startAdornment : (
                <div className={classes.searchIcon}>
                  <TopNavSearchIcon />
                </div>
              ),
              endAdornment : (
                <DisplayNoGroupsButton groupFilterActive={groupFilterActive}
                  onQueryRemove={onQueryRemove}
                  onSelection={onSelection} />
              )
            }}
            autoFocus />
          <ConditionalRender condition={isValidStringLength}>
            <Popper open={isValidStringLength}
              className={classes.suggestionsWrapper}
              transition
              disablePortal>
              <Paper>
                <GroupSearchDropdown fetchNextPage={fetchNextPage}
                  hasNextPage={hasNextPage}
                  onSelection={onSelection}
                  accountTree={accountTree}
                  isFetching={isFetchingNextPage}
                  isLoading={isLoading}
                  data={data} />
              </Paper>
            </Popper>
          </ConditionalRender>
        </div>
      </ClickAwayListener>
    </div>
  );
};

export default GroupSearchInput;
